// SAM-1096 — article Preview page (/settings/articles/:articleId/preview), mock C5: the Home card, the article in
// English and in Español, side by side. Fed by the editor's unsaved form (router state) or by the saved article.
import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useArticles, useAuthors, useCreateArticle, useUpdateArticle, authorDisplayName } from '../../hooks/useArticles';
import { useAds } from '../../hooks/useAds';
import { useUserTypes } from '../../hooks/useUserTypes';
import { useFdCan } from '../../hooks/useFdCan';
import { CmsStateBadge } from '../../components/cms/CmsStateBadge';
import { ArticleHomeCard, ArticleScreen, PhoneFrame } from '../../components/cms/AppPreviews';
import { PageHead, btnGhost, btnPrimary, primaryStyle, selectClass } from '../../components/cms/EditorParts';
import { cmsState, todayInBusinessClock } from '../../utils/cmsState';
import { articleMissing, articlePayload, articleToForm, type ArticleForm } from './cmsForms';
import type { ArticleDraftState } from './ArticleEditorPage';

export const ArticlePreviewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { articleId } = useParams<{ articleId?: string }>();
  const isNew = !articleId || articleId === 'new';
  const draft = (location.state as ArticleDraftState | null)?.draft;
  const { articles, isLoading } = useArticles();
  const { authors } = useAuthors();
  const { ads } = useAds();
  const { userTypes } = useUserTypes();
  const createArticle = useCreateArticle();
  const updateArticle = useUpdateArticle();
  const canEdit = useFdCan('cms', 'edit');
  const [audience, setAudience] = useState('');

  const saved = isNew ? undefined : articles.find((a) => String(a.id) === articleId);
  const form: ArticleForm | null = draft ?? (saved ? articleToForm(saved) : null);
  const editPath = `/settings/articles/${isNew ? 'new' : articleId}`;
  const goEdit = () => navigate(editPath, { state: draft ? ({ draft } satisfies ArticleDraftState) : null });

  const today = todayInBusinessClock();
  const featured = useMemo(
    () => ads
      .filter((a) => cmsState(a, today) === 'published')
      .sort((a, b) => (a.priority || 9e9) - (b.priority || 9e9) || (a.id ?? 0) - (b.id ?? 0))
      .map((a) => ({ title: a.title_en, text: a.short_description_en || '', color: a.background_color, imageKey: a.art_img_url })),
    [ads, today],
  );

  if (!form) {
    if (!isNew && isLoading) return <div className="p-8 text-sm text-gray-500">Loading…</div>;
    return (
      <div className="p-8 text-sm text-gray-600">
        <p className="mt-0">Nothing to preview yet.</p>
        <button type="button" className={btnGhost} onClick={() => navigate(isNew ? '/settings/articles/new' : '/settings/articles')}>Back</button>
      </div>
    );
  }

  const author = authors.find((a) => a.id === form.author_id);
  const data = (lang: 'en' | 'es') => ({
    title: (lang === 'en' ? form.title_en : form.title_es) || (lang === 'en' ? 'Title' : 'Título'),
    content: lang === 'en' ? form.content_en : form.content_es,
    author: author ? authorDisplayName(author) : '',
    imageKey: form.art_img_url,
  });
  const hidden = audience && form.user_types_ids.length > 0 && !form.user_types_ids.includes(Number(audience));
  const state = cmsState(form, today);
  const saving = createArticle.isPending || updateArticle.isPending;

  const publish = async () => {
    if (articleMissing(form, true).length) { goEdit(); return; }
    const payload = articlePayload(form, 'published');
    try {
      if (isNew) await createArticle.mutateAsync(payload);
      else await updateArticle.mutateAsync({ id: Number(articleId), data: payload });
      navigate('/settings/articles');
    } catch {
      // The mutation hooks already show the error toast.
    }
  };

  const phone = (label: string, caption: string, body: React.ReactNode) => (
    <div className="flex flex-col items-center gap-3.5">
      <PhoneFrame height={740} label={label}>{body}</PhoneFrame>
      <div className="text-center">
        <div className="text-sm font-bold">{label}</div>
        <div className="text-xs text-gray-500">{caption}</div>
      </div>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-gray-50 text-gray-900">
      <PageHead
        back="Back to the article"
        onBack={goEdit}
        title="Preview"
        badge={<CmsStateBadge state={state} />}
        aside={<span className="truncate text-[15px] text-gray-700">{form.title_en}</span>}
        actions={
          <>
            <label className="flex items-center gap-2 text-[13px] text-gray-500">
              Sample audience
              <select aria-label="Sample audience" className={`${selectClass} w-[150px]`} value={audience} onChange={(e) => setAudience(e.target.value)}>
                <option value="">Everyone</option>
                {userTypes.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </label>
            <button type="button" className={btnGhost} onClick={goEdit}>Edit</button>
            {canEdit && form.status !== 'published' && (
              <button type="button" className={btnPrimary} style={primaryStyle} disabled={saving} onClick={() => void publish()}>Publish</button>
            )}
          </>
        }
      />
      {hidden && (
        <div role="status" className="mx-8 mt-4 rounded-lg bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
          This audience would not see the article: it is set for other groups.
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mt-5 flex min-h-full justify-center gap-14 border-t border-gray-200 bg-gray-100 px-8 pb-12 pt-7">
        {phone('Home · Weekly', 'The card residents tap', <ArticleHomeCard article={data('en')} featured={featured} />)}
        {phone('Article · English', 'Opens when they tap the card', <ArticleScreen article={data('en')} />)}
        {phone('Article · Español', 'Phones set to Spanish', <ArticleScreen article={data('es')} />)}
      </div>
      </div>
    </div>
  );
};
